import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { AppButton, AppCard, Callout, Checkbox, ConfirmModal, FieldGroup, Radio, Switch } from "../ui";
import { adminListSessions, adminToggleCourseFeature, saveCourseSmartSettings } from "../../utils/smartLearning";
import UnlockRulesFields, { DEFAULT_UNLOCK, cleanUnlockConditions } from "./UnlockRulesFields";

const SOURCES = [
  {
    value: "auto",
    label: "From the syllabus",
    description: "Each batch’s sessions are formed from the topics, at its class length. Attach resources to topics once.",
  },
  {
    value: "custom",
    label: "Authored one by one",
    description: "You write each session of each module yourself, with its own resources and rules.",
  },
];

const rulesOf = (s) => ({
  unlockMode: s?.unlockMode || "afterClass",
  unlockConditions: { ...DEFAULT_UNLOCK, ...(s?.unlockConditions || {}) },
  allowTrainerAdditions: !!s?.allowTrainerAdditions,
});

/**
 * SmartLearningPanel — the course's Smart Learning switch and how its sessions
 * come to be. Everything here applies at once (it is not part of the syllabus
 * save), and changes to how sessions are formed apply to batches started after.
 *
 * Props:
 *   courseId
 *   enabled, onEnabledChange(bool)
 *   blockedReason — why it cannot be switched on yet ('' when it can)
 *   settings      — GET …/smart-settings result
 *   onSettings(next)
 */
const SmartLearningPanel = ({ courseId, enabled, onEnabledChange, blockedReason, settings, onSettings }) => {
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState(null); // { tone, text }
  const [rules, setRules] = useState(() => rulesOf(settings));
  // Switching how sessions are formed is asked first: { to, authored }.
  const [switchTo, setSwitchTo] = useState(null);
  const source = settings?.classSource || "custom";

  useEffect(() => { setRules(rulesOf(settings)); }, [settings]);

  const saved = rulesOf(settings);
  const rulesChanged = JSON.stringify(rules) !== JSON.stringify(saved);

  const toggle = async (next) => {
    setBusy("toggle");
    setMessage(null);
    try {
      await adminToggleCourseFeature(courseId, next);
      onEnabledChange(next);
    } catch (err) {
      setMessage({ tone: "danger", text: err?.response?.data?.message || "Smart Learning could not be switched." });
    } finally {
      setBusy("");
    }
  };

  const save = async (patch, ok) => {
    setBusy("settings");
    setMessage(null);
    try {
      const res = await saveCourseSmartSettings(courseId, patch);
      onSettings(res.settings);
      const n = res.updatedSessions || 0;
      setMessage({ tone: "success", text: n ? `${ok} ${n} session${n === 1 ? "" : "s"} not yet taught follow the new rules.` : ok });
    } catch (err) {
      setMessage({ tone: "danger", text: err?.response?.data?.message || "The change was not saved." });
    } finally {
      setBusy("");
    }
  };

  // A course's existing sessions are never converted by themselves (they keep
  // running in the batches that have them), so say so before switching.
  const askSwitch = async (to) => {
    if (to === source) return;
    let authored = 0;
    try {
      const data = await adminListSessions({ courseId });
      authored = (data.sessions || []).filter((s) => !s.batch).length;
    } catch {
      /* the question still makes sense without the count */
    }
    setSwitchTo({ to, authored });
  };

  const switchMessage = (s) => {
    if (!s) return "";
    if (s.to === "auto") {
      return [
        "Batches started from now will have their sessions formed from the syllabus topics, at each batch’s class length.",
        s.authored
          ? `The ${s.authored} session${s.authored === 1 ? "" : "s"} written by hand stay as they are, and batches already running keep them. Their resources are not moved — attach resources to topics for new batches.`
          : "",
        "You can switch back at any time.",
      ].filter(Boolean).join(" ");
    }
    return [
      "Batches started from now will use the sessions you write in each module.",
      s.authored ? "" : "This course has none yet — add them before the next batch starts, or it will have no sessions.",
      "Batches already running keep the sessions they have.",
    ].filter(Boolean).join(" ");
  };

  return (
    <AppCard as="section" aria-labelledby="sb-smart-heading">
      <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
        <div className="tw-flex tw-flex-col tw-gap-1">
          <h2 id="sb-smart-heading" className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-[0.9375rem] tw-font-semibold tw-text-content">
            <Sparkles size={16} aria-hidden="true" className="tw-text-primary" /> Smart Learning
          </h2>
          <p className="tw-m-0 tw-text-xs tw-text-content-muted">
            A guided path for students: each session opens the next, with its resources.
          </p>
        </div>

        <Switch
          checked={!!enabled}
          onChange={toggle}
          disabled={busy === "toggle" || (!enabled && !!blockedReason)}
          label="Smart Learning for this course"
          description={enabled ? "On — batches started from now are planned as Smart Learning sessions." : "Off"}
        />
        {!enabled && blockedReason && <Callout tone="info">{blockedReason}</Callout>}
        {message && <Callout tone={message.tone} role={message.tone === "danger" ? "alert" : "status"}>{message.text}</Callout>}

        {enabled && (
          <>
            <FieldGroup legend="How are sessions formed?">
              <div className="tw-flex tw-flex-col tw-gap-2">
                {SOURCES.map((s) => (
                  <Radio
                    key={s.value}
                    variant="card"
                    name="sb-smart-source"
                    value={s.value}
                    checked={source === s.value}
                    onChange={() => askSwitch(s.value)}
                    disabled={!!busy}
                    labelClassName="tw-font-semibold"
                    label={s.label}
                    description={s.description}
                  />
                ))}
              </div>
            </FieldGroup>

            {source === "auto" ? (
              <details open className="tw-rounded-md tw-border tw-border-solid tw-border-line tw-px-3 tw-py-2">
                <summary className="tw-cursor-pointer tw-select-none tw-text-[0.8125rem] tw-font-semibold tw-text-content tw-focus-ring">
                  Unlock rules for every session
                </summary>
                <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-4">
                  <UnlockRulesFields
                    value={rules}
                    onChange={(v) => setRules((r) => ({ ...r, unlockMode: v.unlockMode, unlockConditions: v.unlockConditions }))}
                    disabled={!!busy}
                  />
                  <Checkbox
                    checked={rules.allowTrainerAdditions}
                    onChange={(e) => setRules((r) => ({ ...r, allowTrainerAdditions: e.target.checked }))}
                    disabled={!!busy}
                    label="Let trainers add documents or assignments while teaching"
                  />
                  <div className="tw-flex tw-justify-end">
                    <AppButton
                      size="sm"
                      disabled={!rulesChanged || !!busy}
                      onClick={() => save({
                        unlockMode: rules.unlockMode,
                        unlockConditions: cleanUnlockConditions(rules.unlockConditions),
                        allowTrainerAdditions: rules.allowTrainerAdditions,
                      }, "Rules saved.")}
                    >
                      {busy === "settings" ? "Saving…" : "Save rules"}
                    </AppButton>
                  </div>
                </div>
              </details>
            ) : (
              <p className="tw-m-0 tw-text-xs tw-text-content-muted">Open a module and use <strong className="tw-font-semibold tw-text-content">Add session</strong> under its topics. Each session takes its documents, assignments, quizzes, coding questions and unlock rules.</p>
            )}
          </>
        )}
      </div>

      <ConfirmModal
        show={!!switchTo}
        onHide={() => setSwitchTo(null)}
        onConfirm={() => {
          const to = switchTo.to;
          setSwitchTo(null);
          save({ classSource: to }, "Saved — batches started from now use it.");
        }}
        title={switchTo?.to === "auto" ? "Form sessions from the syllabus?" : "Write sessions by hand?"}
        message={switchMessage(switchTo)}
        confirmText="Switch"
        variant="info"
      />
    </AppCard>
  );
};

export default SmartLearningPanel;
